/**
 * Chart configurations live on the Report template (Report.charts[]). A chart
 * plots one X field (a date or a category) against any of the report's numeric
 * fields; the viewer toggles the Y fields on and off, the template supplies
 * the defaults. Shared by the report screen (rendering) and the Report Editor
 * (authoring). Reserved for the dashboard: `dashboard.enabled` — a chart
 * flagged there will be offered as a dashboard tile in a later release.
 */

export type ReportChartType = 'line' | 'bar';
export type ReportChartAggregate = 'sum' | 'avg';
export type ReportChartBucket = 'hour' | 'day' | 'week' | 'month';

export interface ReportChartConfig {
  key: string;
  name: string;
  /** Default 'line'. The viewer can switch between line and bar. */
  type?: ReportChartType;
  /** Field path plotted on X (date/dateTime fields are bucketed; strings are categories). */
  xPath: string;
  /** Date-typed X fields are grouped into this bucket. Default 'day'. */
  xBucket?: ReportChartBucket;
  /** Optional field that splits rows into series (e.g. locationName). */
  seriesPath?: string;
  /** Y fields toggled on when the chart opens. */
  yPaths: string[];
  /** How rows sharing an X value (and series) combine. Default 'sum'. */
  aggregate?: ReportChartAggregate;
  dashboard?: {
    enabled?: boolean;
  };
}

export const CHART_TYPES: Array<{ key: ReportChartType; name: string }> = [
  { key: 'line', name: 'Line' },
  { key: 'bar', name: 'Bar' },
];

export const CHART_AGGREGATES: Array<{ key: ReportChartAggregate; name: string }> = [
  { key: 'sum', name: 'Sum' },
  { key: 'avg', name: 'Average' },
];

export const CHART_BUCKETS: Array<{ key: ReportChartBucket; name: string }> = [
  { key: 'hour', name: 'Hour' },
  { key: 'day', name: 'Day' },
  { key: 'week', name: 'Week' },
  { key: 'month', name: 'Month' },
];

// Field types whose values are stored in cents.
const CENTS_TYPES = ['currency', 'currencyDetail', 'currencyInverse', 'points'];
const CURRENCY_TYPES = ['currency', 'currencyDetail', 'currencyInverse', 'currencyDollars'];
const NUMERIC_TYPES = [
  'number', 'numberInverse', 'numberDetail',
  'currency', 'currencyDetail', 'currencyInverse', 'currencyDollars',
  'percent', 'percentage', 'points',
];
const DATE_TYPES = ['date', 'dateTime', 'dateTimeYMD', 'dateTimeGeneric', 'dateTimeDay', 'dateTimeShort'];

export function isNumericField(field: any): boolean {
  return !!field && NUMERIC_TYPES.indexOf(field.type) !== -1;
}

export function isCurrencyField(field: any): boolean {
  return !!field && CURRENCY_TYPES.indexOf(field.type) !== -1;
}

export function isPercentField(field: any): boolean {
  return !!field && (field.type === 'percent' || field.type === 'percentage');
}

export function isDateField(field: any): boolean {
  return !!field && DATE_TYPES.indexOf(field.type) !== -1;
}

/** Fields a chart can plot on Y (numeric, unless the field opts out with chartable: false). */
export function chartableYFields(fields: any[]): any[] {
  return (fields || []).filter(f => isNumericField(f) && f.chartable !== false);
}

/** Fields a chart can plot on X: dates first, then strings/ids usable as categories. */
export function chartableXFields(fields: any[]): any[] {
  const all = fields || [];
  return all.filter(isDateField).concat(all.filter(f => f && !isDateField(f) && !isNumericField(f)));
}

/**
 * Convert a stored value into the number the chart plots: cents become
 * dollars, inverse fields flip sign, ratios become percentages.
 */
export function plotValue(field: any, raw: any): number | null {
  if (raw === undefined || raw === null || raw === '') {
    return null;
  }
  const value = Number(raw);
  if (isNaN(value)) {
    return null;
  }
  let scaled = value;
  if (CENTS_TYPES.indexOf(field.type) !== -1) {
    scaled = value / 100;
  }
  if (field.type === 'currencyInverse' || field.type === 'numberInverse') {
    scaled = scaled * -1;
  }
  if (isPercentField(field)) {
    scaled = value * 100;
  }
  return scaled;
}

/** Format a plotted (already scaled) value for ticks and tooltips. */
export function formatPlotValue(field: any, value: number): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }
  if (isCurrencyField(field)) {
    const abs = Math.abs(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return (value < 0 ? '-$' : '$') + abs;
  }
  if (isPercentField(field)) {
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 }) + '%';
  }
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

/**
 * Calculated report fields (field.calculated + field.formula, e.g.
 * "$total / $customerCount") are ratios: they must never be summed across
 * rows. The chart sums the base fields a formula references per bucket and
 * evaluates the formula on those sums — exact for ratios of sums, the same
 * rule the report totals row uses.
 */
export function isCalculatedField(field: any): boolean {
  return !!field && field.calculated === true && typeof field.formula === 'string' && field.formula.length > 0;
}

/** Field paths referenced by a formula ("$total / $customerCount" → ["total", "customerCount"]). */
export function formulaReferences(formula: string): string[] {
  const refs: string[] = [];
  const pattern = /\$([a-zA-Z_][a-zA-Z0-9_]*)/g;
  let match = pattern.exec(formula || '');
  while (match) {
    if (refs.indexOf(match[1]) === -1) {
      refs.push(match[1]);
    }
    match = pattern.exec(formula);
  }
  return refs;
}

/**
 * Evaluate a formula against a value lookup. Only numbers, $references and
 * + - * / ( ) are allowed; anything else, a missing value, or a division by
 * zero yields null.
 */
export function evaluateFormula(formula: string, getValue: (path: string) => number | null | undefined): number | null {
  let valid = true;
  const resolved = (formula || '').replace(/\$([a-zA-Z_][a-zA-Z0-9_]*)/g, (_m, path) => {
    const value = getValue(path);
    if (value === null || value === undefined || isNaN(Number(value))) {
      valid = false;
      return '0';
    }
    return '(' + Number(value) + ')';
  });
  if (!valid || !/^[0-9+\-*/().\s]+$/.test(resolved)) {
    return null;
  }
  try {
    // tslint:disable-next-line:no-eval
    const result = new Function('return (' + resolved + ');')();
    return typeof result === 'number' && isFinite(result) ? result : null;
  } catch (e) {
    return null;
  }
}

/** A URL-safe key derived from a chart name, e.g. "Daily Sales" → "daily-sales". */
export function chartKeyFromName(name: string): string {
  return (name || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Configs that can actually render against the report's fields. */
export function validCharts(charts: any, fields: any[]): ReportChartConfig[] {
  if (!Array.isArray(charts)) {
    return [];
  }
  const byPath: { [path: string]: any } = {};
  for (const field of fields || []) {
    if (field && field.path) {
      byPath[field.path] = field;
    }
  }
  return charts.filter(chart =>
    chart && chart.key && chart.name && chart.xPath && byPath[chart.xPath]
    && Array.isArray(chart.yPaths));
}
